import { Component, Inject, OnInit, inject, signal } from '@angular/core';

import { ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
// Services
import { CustomParamsService } from '@shared/services/entities/customParams.service';
import { PlacesService } from '@shared/services/entities/places.service';
import { SpecialtyService } from '@shared/services/entities/specialty.service';
import { CustomMessageService } from '@shared/components/custom-message/custom-message.service';
// Models
import { User } from '@shared/models/entities/users.model';
import { CustomParams } from '@shared/models/entities/customParams.model';
// Utils
import { sortAlphabetically } from '@shared/utils/sort.utils';

export interface UserCustomParamsDialogData {
  user: User;
}

/**
 * Diálogo "Sitios y especialidades" de un usuario (tabla `customparams`).
 *
 * Se abre desde el botón de cada fila del módulo de usuarios. Carga los
 * catálogos de sitios y especialidades, más el registro existente del
 * usuario (si lo hay), y guarda con upsert: un registro único por usuario.
 */
@Component({
  selector: 'app-user-custom-params-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatDividerModule,
    MatProgressSpinnerModule
],
  templateUrl: './user-custom-params-dialog.component.html',
  styleUrl: './user-custom-params-dialog.component.scss',
})
export class UserCustomParamsDialogComponent implements OnInit {

  private readonly fb                  = inject(FormBuilder);
  private readonly customParamsService = inject(CustomParamsService);
  private readonly placesService       = inject(PlacesService);
  private readonly specialtyService    = inject(SpecialtyService);
  private readonly messageService      = inject(CustomMessageService);
  private readonly dialogRef           = inject(MatDialogRef<UserCustomParamsDialogComponent>);

  // Catálogos para los multi-selects.
  availablePlaces      = signal<any[]>([]);
  availableSpecialties = signal<any[]>([]);

  // Registro existente del usuario (null si aún no tiene).
  private existing: CustomParams | null = null;

  isLoading = signal(true);
  isSaving  = signal(false);

  form!: FormGroup;

  constructor(@Inject(MAT_DIALOG_DATA) public data: UserCustomParamsDialogData) {}

  get user(): User {
    return this.data.user;
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      places:      [[] as string[]],
      specialties: [[] as string[]],
    });
    this.loadAll();
  }

  private async loadAll(): Promise<void> {
    this.isLoading.set(true);
    try {
      // Catálogos + registro del usuario, en paralelo.
      const [, , current] = await Promise.all([
        this.placesService.search({ searchType: 'default', condition: { deleted: false } }),
        this.specialtyService.search({ searchType: 'default', condition: { deleted: false } }),
        this.customParamsService.getByUserId(this.user.id),
      ]);

      this.availablePlaces.set(sortAlphabetically(this.placesService.listData(), 'name'));
      this.availableSpecialties.set(sortAlphabetically(this.specialtyService.listData(), 'name'));

      this.existing = current;
      this.form.patchValue({
        places:      Array.isArray(current?.places) ? current!.places : [],
        specialties: Array.isArray(current?.specialties) ? current!.specialties : [],
      });
    } catch {
      await this.messageService.open({
        title: 'Error', type: 'error',
        message: 'No se pudieron cargar las ubicaciones y especialidades.',
        dismissBtn: true,
      });
      this.dialogRef.close(false);
      return;
    } finally {
      this.isLoading.set(false);
    }
  }

  /** ¿Están marcadas todas las opciones del catálogo? */
  allSelected(control: 'places' | 'specialties', options: { id: string }[]): boolean {
    const value: string[] = this.form?.value?.[control] ?? [];
    return options.length > 0 && options.every(o => value.includes(o.id));
  }

  /** Un solo botón: marca todas o, si ya están todas, las quita. */
  toggleAll(control: 'places' | 'specialties', options: { id: string }[]): void {
    const next = this.allSelected(control, options) ? [] : options.map(o => o.id);
    this.form.get(control)?.setValue(next);
    this.form.get(control)?.markAsDirty();
  }

  async onSave(): Promise<void> {
    if (this.isSaving() || this.isLoading()) return;

    const places      = (this.form.value.places as string[]) ?? [];
    const specialties = (this.form.value.specialties as string[]) ?? [];

    this.isSaving.set(true);
    try {
      await this.customParamsService.saveForUser(this.user.id, places, specialties);
      await this.messageService.open({
        title: '¡Listo!', type: 'success',
        message: `Se guardaron las ubicaciones y especialidades de <b>${this.user.name}</b>.`,
        duration: 2500,
      });
      this.dialogRef.close(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No se pudieron guardar los cambios.';
      await this.messageService.open({
        title: 'Error', type: 'error', message, dismissBtn: true,
      });
    } finally {
      this.isSaving.set(false);
    }
  }

  onClose(): void {
    this.dialogRef.close(false);
  }
}
